import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "@/packages/utils";
import type { ChatPanelMessagesProps } from "./types";

export function ChatPanelMessages({ messages }: ChatPanelMessagesProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation(["chat"]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  });

  if (messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-4">
        <p className="text-sm text-muted">{t("chat:assistant.emptyState")}</p>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4">
      {messages.map((message) => (
        <motion.div
          key={message.id}
          className={`flex flex-col ${message.role === "user" ? "items-end" : "items-start"}`}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
        >
          <div
            className={
              message.role === "user"
                ? "bg-accent text-white rounded-2xl rounded-br-sm px-4 py-2 max-w-[85%]"
                : "bg-card text-primary rounded-2xl rounded-bl-sm px-4 py-2 max-w-[85%]"
            }
          >
            <p className="text-sm whitespace-pre-wrap">{message.content}</p>
          </div>
          {message.timestamp && (
            <span className="text-xs text-muted mt-1">{message.timestamp}</span>
          )}
        </motion.div>
      ))}
      <div ref={bottomRef} />
    </div>
  );
}
